import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, BedDouble, Bath, CalendarClock, Building2, Check, ArrowLeft, Ruler, Layers, FileDown, Pencil, Loader2, Home } from "lucide-react";
import { getDevelopment, submitEnquiry } from "../api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { DevEditor } from "../components/admin/AdminAgency";
import Seo from "../components/Seo";

const fmt = (n) => (n ? `\u00A3${Number(n).toLocaleString()}` : "POA");
const sColor = { "Now Selling": "bg-green-500", "Coming Soon": "bg-amber-500", "Final Units": "bg-red-500", "Sold Out": "bg-gray-400" };
const availColor = { Available: "bg-green-100 text-green-700", Reserved: "bg-amber-100 text-amber-700", Sold: "bg-gray-200 text-gray-500" };

const DevelopmentDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user && (user.role === "admin" || user.role === "agent");
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const load = () => getDevelopment(id).then((x) => { setD(x); setActive(0); }).catch(() => setNotFound(true));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast({ title: "Please add your name and email" }); return; }
    setBusy(true);
    try {
      await submitEnquiry({ ...form, source: `development-${d.name}`, propertyTitle: d.name });
      toast({ title: "Interest registered", description: `We'll be in touch about ${d.name}.` });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch { toast({ title: "Something went wrong", description: "Please try again." }); }
    finally { setBusy(false); }
  };

  if (notFound) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-5 text-center">
      <h1 className="font-display text-[30px] text-[#2b2b2b] mb-3">Development not found</h1>
      <Link to="/developments" className="px-7 py-3 rounded-full bg-[#b8935f] text-white text-[14px] font-500">All developments</Link>
    </div>
  );
  if (!d) return <div className="min-h-[60vh] flex items-center justify-center"><p className="font-display text-[22px] text-[#8a8a8a]">Loading…</p></div>;

  const gallery = (d.images && d.images.length ? d.images : (d.image ? [d.image] : []));
  const units = d.unitTypes || [];

  return (
    <div className="bg-[#faf7f2]" data-testid="development-detail">
      <Seo title={`${d.name} — New Homes in ${d.location}`} description={(d.description || `New homes at ${d.name}, ${d.location}. Prices from ${fmt(d.priceFrom)}.`).slice(0, 300)} path={`/developments/${d.id}`} image={d.image} type="article" />

      <div className="max-w-[1180px] mx-auto px-5 md:px-8 pt-8 flex items-center justify-between">
        <Link to="/developments" className="inline-flex items-center gap-2 text-[14px] text-[#6a6a6a] hover:text-[#b8935f] transition-colors"><ArrowLeft size={16} /> All developments</Link>
        {isAdmin && <button onClick={() => setEditing(true)} data-testid="dev-detail-edit" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1c1c1e] text-white text-[13px] font-500 hover:bg-black transition-colors"><Pencil size={14} /> Edit development</button>}
      </div>

      {/* Gallery */}
      <div className="max-w-[1180px] mx-auto px-5 md:px-8 pt-6">
        <div className="relative rounded-2xl overflow-hidden aspect-[16/9] bg-[#eee7db]">
          {gallery[active] && <img src={gallery[active]} alt={d.name} className="w-full h-full object-cover" data-testid="dev-gallery-main" />}
          <span className="absolute top-4 left-4 flex items-center gap-1.5 text-[12px] px-3 py-1 rounded-full bg-white/95 text-[#2b2b2b] font-500"><span className={`w-2 h-2 rounded-full ${sColor[d.status] || "bg-gray-400"}`} />{d.status}</span>
        </div>
        {gallery.length > 1 && (
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
            {gallery.map((img, i) => (
              <button key={i} onClick={() => setActive(i)} className={`shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 ${active === i ? "border-[#b8935f]" : "border-transparent"}`} data-testid={`dev-thumb-${i}`}>
                <img src={img} alt={`${d.name} ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-[1180px] mx-auto px-5 md:px-8 py-12 grid lg:grid-cols-[1.6fr_1fr] gap-12">
        <div>
          <p className="text-[12px] tracking-[0.18em] uppercase text-[#b8935f] mb-1">{d.developer || "Anadear New Homes"}</p>
          <h1 className="font-display text-[34px] md:text-[46px] font-300 text-[#2b2b2b]">{d.name}</h1>
          <p className="flex items-center gap-2 text-[15px] text-[#6a6a6a] mt-2"><MapPin size={16} className="text-[#b8935f]" /> {d.location}{d.postcode ? `, ${d.postcode}` : ""}</p>

          <div className="flex flex-wrap gap-8 mt-6 pb-8 border-b border-[#e6ddd0]">
            <div><p className="text-[11px] uppercase text-[#a0a0a0]">Prices from</p><p className="font-display text-[24px] text-[#2b2b2b]">{fmt(d.priceFrom)}</p></div>
            {d.bedsRange && <div><p className="text-[11px] uppercase text-[#a0a0a0]">Homes</p><p className="font-display text-[24px] text-[#2b2b2b] flex items-center gap-1.5"><BedDouble size={18} className="text-[#b8935f]" /> {d.bedsRange}</p></div>}
            {d.completion && <div><p className="text-[11px] uppercase text-[#a0a0a0]">Completion</p><p className="font-display text-[24px] text-[#2b2b2b] flex items-center gap-1.5"><CalendarClock size={18} className="text-[#b8935f]" /> {d.completion}</p></div>}
            {d.tenure && <div><p className="text-[11px] uppercase text-[#a0a0a0]">Tenure</p><p className="font-display text-[24px] text-[#2b2b2b]">{d.tenure}</p></div>}
            {d.epc && <div><p className="text-[11px] uppercase text-[#a0a0a0]">EPC</p><p className="font-display text-[24px] text-[#2b2b2b]">{d.epc}</p></div>}
          </div>

          {d.description && (
            <div className="py-8 border-b border-[#e6ddd0]">
              <h2 className="font-display text-[24px] font-500 text-[#2b2b2b] mb-4">About this development</h2>
              <p className="text-[15px] text-[#6a6a6a] leading-relaxed whitespace-pre-line">{d.description}</p>
            </div>
          )}

          {d.units > 0 && (
            <div className="py-8 border-b border-[#e6ddd0]">
              <div className="flex justify-between text-[13px] text-[#6a6a6a] mb-1.5"><span>{d.unitsAvailable} of {d.units} homes available</span><span>{d.soldPct}% reserved</span></div>
              <div className="h-2.5 rounded-full bg-[#eee7db] overflow-hidden"><div className="h-full bg-[#b8935f]" style={{ width: `${d.soldPct}%` }} /></div>
            </div>
          )}

          {/* Units list */}
          {units.length > 0 && (
            <div className="py-8 border-b border-[#e6ddd0]" data-testid="dev-units-list">
              <h2 className="font-display text-[24px] font-500 text-[#2b2b2b] mb-5 flex items-center gap-2"><Home size={20} className="text-[#b8935f]" /> Available homes</h2>
              <div className="rounded-xl border border-[#f0ece5] overflow-hidden bg-white">
                <div className="hidden sm:grid grid-cols-[1.5fr_repeat(4,1fr)] gap-2 px-4 py-3 bg-[#faf7f2] text-[11px] uppercase tracking-wide text-[#a0a0a0]">
                  <span>Home</span><span>Beds</span><span>Baths</span><span>Size</span><span>Price</span>
                </div>
                {units.map((u, i) => (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-[1.5fr_repeat(4,1fr)] gap-2 px-4 py-3 border-t border-[#f5f1ea] items-center text-[14px]" data-testid={`dev-unit-${i}`}>
                    <div>
                      <p className="text-[#2b2b2b] font-500">{u.name || "Home"}</p>
                      <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full ${availColor[u.availability] || "bg-gray-100 text-gray-500"}`}>{u.availability || "Available"}</span>
                    </div>
                    <span className="text-[#6a6a6a] flex items-center gap-1"><BedDouble size={14} className="text-[#b8935f] sm:hidden" />{u.beds || "—"}</span>
                    <span className="text-[#6a6a6a] flex items-center gap-1"><Bath size={14} className="text-[#b8935f] sm:hidden" />{u.baths || "—"}</span>
                    <span className="text-[#6a6a6a] flex items-center gap-1"><Ruler size={14} className="text-[#b8935f] sm:hidden" />{u.size ? `${u.size} sq ft` : "—"}</span>
                    <span className="text-[#b8935f] font-500">{fmt(u.price)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Floorplans */}
          {(d.floorplans || []).length > 0 && (
            <div className="py-8 border-b border-[#e6ddd0]" data-testid="dev-floorplans-list">
              <h2 className="font-display text-[24px] font-500 text-[#2b2b2b] mb-5 flex items-center gap-2"><Layers size={20} className="text-[#b8935f]" /> Floorplans</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {d.floorplans.map((fp, i) => <img key={i} src={fp} alt={`Floorplan ${i + 1}`} className="rounded-xl border border-[#f0ece5] w-full" />)}
              </div>
            </div>
          )}

          {/* Features & amenities */}
          {((d.features || []).length > 0 || (d.amenities || []).length > 0) && (
            <div className="py-8 grid sm:grid-cols-2 gap-8">
              {(d.features || []).length > 0 && (
                <div>
                  <h3 className="font-display text-[19px] font-500 text-[#2b2b2b] mb-4 flex items-center gap-2"><Building2 size={18} className="text-[#b8935f]" /> Specification</h3>
                  <ul className="space-y-2">{d.features.map((x) => <li key={x} className="flex items-start gap-2 text-[14px] text-[#4a4a4a]"><Check size={16} className="text-[#b8935f] mt-0.5 shrink-0" /> {x}</li>)}</ul>
                </div>
              )}
              {(d.amenities || []).length > 0 && (
                <div>
                  <h3 className="font-display text-[19px] font-500 text-[#2b2b2b] mb-4 flex items-center gap-2"><Check size={18} className="text-[#b8935f]" /> Amenities</h3>
                  <ul className="space-y-2">{d.amenities.map((x) => <li key={x} className="flex items-start gap-2 text-[14px] text-[#4a4a4a]"><Check size={16} className="text-[#b8935f] mt-0.5 shrink-0" /> {x}</li>)}</ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar enquiry */}
        <aside>
          <div className="sticky top-[100px] bg-white rounded-2xl border border-[#f0ece5] shadow-[0_12px_40px_rgba(0,0,0,0.06)] p-7" data-testid="dev-enquiry-card">
            <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-1">Register your interest</h3>
            <p className="text-[13px] text-[#8a8a8a] mb-5">Be first to hear about availability, pricing and launch events at {d.name}.</p>
            <form onSubmit={submit} className="space-y-3">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full h-[48px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:border-[#b8935f]" data-testid="dev-enquiry-name" />
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" className="w-full h-[48px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:border-[#b8935f]" data-testid="dev-enquiry-email" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone number" className="w-full h-[48px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:border-[#b8935f]" />
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder={`I'm interested in ${d.name}…`} className="w-full px-4 py-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:border-[#b8935f] resize-none" />
              <button type="submit" disabled={busy} className="w-full h-[50px] rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="dev-enquiry-submit">{busy ? <Loader2 size={16} className="animate-spin" /> : null} Register interest</button>
            </form>
            {d.brochureUrl && <a href={d.brochureUrl} target="_blank" rel="noreferrer" className="mt-3 w-full inline-flex items-center justify-center gap-2 h-[46px] rounded-lg border border-[#e2d8c8] text-[#2b2b2b] text-[14px] font-500 hover:border-[#b8935f] hover:text-[#b8935f] transition-colors"><FileDown size={16} /> Download brochure</a>}
          </div>
        </aside>
      </div>

      {editing && <DevEditor initial={d} onClose={() => setEditing(false)} onSaved={load} />}
    </div>
  );
};

export default DevelopmentDetail;
